import type { AccountingReportJson, ReportColumnUnit, ReportComparisonMode } from '@query'
import { hasChangeColumns, type PeriodColumn, type ResolvedPeriod } from './periodUtils'

export const labelCountForPeriodColumns = (
  periodColumnCount: number,
  comparisonEnabled: boolean
): number => {
  if (!comparisonEnabled || periodColumnCount <= 1) return periodColumnCount
  return periodColumnCount * 2 - 1
}

const periodValueIndex = (
  periodIndex: number,
  periodCount: number,
  comparisonEnabled: boolean
): number => (comparisonEnabled && periodCount > 1 ? periodIndex * 2 : periodIndex)

export const buildReportColumnLabels = (
  resolvedColumns: ResolvedPeriod[],
  comparisonEnabled: boolean,
  changeLabel: string
): string[] => {
  if (!comparisonEnabled || resolvedColumns.length <= 1) {
    return resolvedColumns.map(column => column.label)
  }

  const labels: string[] = []
  resolvedColumns.forEach((column, index) => {
    labels.push(column.label)
    if (index < resolvedColumns.length - 1) {
      labels.push(changeLabel)
    }
  })
  return labels
}

export const buildReportColumnUnits = (
  periodColumnCount: number,
  comparisonEnabled: boolean,
  comparisonMode: ReportComparisonMode
): ReportColumnUnit[] => {
  if (!comparisonEnabled || periodColumnCount <= 1) {
    return Array.from({ length: periodColumnCount }, () => 'amount')
  }
  const units: ReportColumnUnit[] = []
  for (let index = 0; index < periodColumnCount; index += 1) {
    units.push('amount')
    if (index < periodColumnCount - 1) {
      units.push(comparisonMode)
    }
  }
  return units
}

export const alignAccountingReportValues = (
  oldValues: (number | null)[],
  lastColumnIds: string[],
  lastComparisonEnabled: boolean,
  currentColumnIds: string[],
  currentComparisonEnabled: boolean,
  lastComparisonMode: ReportComparisonMode,
  currentComparisonMode: ReportComparisonMode
): (number | null)[] => {
  const newLength = labelCountForPeriodColumns(currentColumnIds.length, currentComparisonEnabled)
  const result: (number | null)[] = Array.from({ length: newLength }, () => null)
  const oldIdToPeriodIndex = new Map(lastColumnIds.map((id, index) => [id, index]))

  currentColumnIds.forEach((id, currentPeriodIndex) => {
    const oldPeriodIndex = oldIdToPeriodIndex.get(id)
    if (oldPeriodIndex === undefined) return

    const oldValueIndex = periodValueIndex(
      oldPeriodIndex,
      lastColumnIds.length,
      lastComparisonEnabled
    )
    const newValueIndex = periodValueIndex(
      currentPeriodIndex,
      currentColumnIds.length,
      currentComparisonEnabled
    )
    if (oldValueIndex < oldValues.length) {
      result[newValueIndex] = oldValues[oldValueIndex]
    }
  })

  if (
    lastComparisonEnabled &&
    currentComparisonEnabled &&
    lastComparisonMode === currentComparisonMode
  ) {
    for (let index = 0; index < currentColumnIds.length - 1; index += 1) {
      const leftId = currentColumnIds[index]
      const rightId = currentColumnIds[index + 1]
      const leftOldIndex = oldIdToPeriodIndex.get(leftId)
      const rightOldIndex = oldIdToPeriodIndex.get(rightId)
      if (leftOldIndex === undefined || rightOldIndex !== leftOldIndex + 1) continue

      const oldChangeIndex = leftOldIndex * 2 + 1
      const newChangeIndex = index * 2 + 1
      if (oldChangeIndex < oldValues.length) {
        result[newChangeIndex] = oldValues[oldChangeIndex]
      }
    }
  }

  return result
}

export type AccountingReportDisplayJson = Omit<AccountingReportJson, 'rows'> & {
  rows: Array<
    Omit<AccountingReportJson['rows'][number], 'values' | 'accounts'> & {
      values: (number | null)[]
      accounts: Array<
        Omit<NonNullable<AccountingReportJson['rows'][number]['accounts']>[number], 'values'> & {
          values: (number | null)[]
        }
      >
    }
  >
}

export const buildOptimisticAccountingReport = ({
  data,
  lastColumns,
  lastComparisonEnabled,
  lastComparisonMode,
  currentColumns,
  currentResolvedColumns,
  currentComparisonEnabled,
  currentComparisonMode,
  changeLabel
}: {
  data: AccountingReportJson
  lastColumns: PeriodColumn[]
  lastComparisonEnabled: boolean
  lastComparisonMode: ReportComparisonMode
  currentColumns: PeriodColumn[]
  currentResolvedColumns: ResolvedPeriod[]
  currentComparisonEnabled: boolean
  currentComparisonMode: ReportComparisonMode
  changeLabel: string
}): AccountingReportDisplayJson => {
  const labels = buildReportColumnLabels(
    currentResolvedColumns,
    currentComparisonEnabled,
    changeLabel
  )

  const lastColumnIds = lastColumns.map(column => column.id)
  const currentColumnIds = currentColumns.map(column => column.id)

  const align = (values: (number | null)[]) =>
    alignAccountingReportValues(
      values,
      lastColumnIds,
      lastComparisonEnabled,
      currentColumnIds,
      currentComparisonEnabled,
      lastComparisonMode,
      currentComparisonMode
    )

  return {
    ...data,
    comparison_mode: currentComparisonEnabled ? currentComparisonMode : 'amount',
    column_units: buildReportColumnUnits(
      currentColumns.length,
      currentComparisonEnabled,
      currentComparisonMode
    ),
    labels,
    rows: data.rows.map(row => ({
      ...row,
      values: align(row.values),
      accounts: (row.accounts ?? []).map(account => ({
        ...account,
        values: align(account.values)
      }))
    }))
  }
}

export const needsOptimisticColumnLayout = (
  lastColumns: PeriodColumn[],
  lastComparisonEnabled: boolean,
  lastComparisonMode: ReportComparisonMode,
  currentColumns: PeriodColumn[],
  currentComparisonEnabled: boolean,
  currentComparisonMode: ReportComparisonMode
): boolean => {
  if (lastColumns.length !== currentColumns.length) return true
  if (lastComparisonEnabled !== currentComparisonEnabled) return true
  if (lastComparisonMode !== currentComparisonMode) return true
  return lastColumns.some((column, index) => column.id !== currentColumns[index]?.id)
}

export const resolveChangeColumnLabel = (
  labels: string[] | undefined,
  periodColumnCount: number,
  fallback: string
): string => (labels && hasChangeColumns(periodColumnCount, labels.length) ? labels[1] : fallback)
